import { useMutation } from '@tanstack/react-query';
import type { SMPQueryParams } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import type { ViomeApiError } from '@viome/viome-base/src/api/VIResponse.model';
import { useIframeModeStore } from '@/hooks/useIframeModeStore';
import { requestPaymentUpdateUrl } from '../api/paymentUpdate';
import { navigateExternal } from '../utils/navigateExternal';
import { appendRedirectUrl, buildReturnUrl } from '../utils/paymentUpdateReturn';

/**
 * 'unavailable' — Loop or our backend could not be reached (5xx / network):
 *   the page offers the email-link fallback.
 * 'generic'     — anything else (400 ownership/malformed id, 404, missing URL).
 */
export type PaymentUpdateErrorKind = 'unavailable' | 'generic';

export const classifyPaymentUpdateError = (error: unknown): PaymentUpdateErrorKind => {
  const status = (error as Partial<ViomeApiError> | null | undefined)?.statusCode;
  if (typeof status !== 'number') return 'generic';
  if (status === -1 || status >= 500) return 'unavailable';
  return 'generic';
};

interface UsePaymentUpdateRedirectOptions {
  onError?: (error: unknown) => void;
}

/**
 * "Update card" flow: fetch a fresh Loop hosted-page URL for this
 * subscription, append our return URL, and leave the SPA. The URL is held
 * only inside the mutation's success callback and is never logged or stored.
 *
 * All state comes from the mutation itself. A successful call means the page
 * is unloading, so `isPending` stays true (`isSuccess`) until it does. Calling
 * `start()` again clears a previous error: TanStack resets the mutation on
 * every `mutate`, so no manual reset is needed.
 */
export const usePaymentUpdateRedirect = (
  params: SMPQueryParams,
  options?: UsePaymentUpdateRedirectOptions,
) => {
  const iframe = useIframeModeStore();

  const mutation = useMutation<string, unknown, void>({
    mutationFn: () => requestPaymentUpdateUrl(params),
    onSuccess: (updatePaymentUrl) => {
      const returnTo = buildReturnUrl(window.location, {
        subscriptionId: String(params.subscriptionId),
        iframe,
      });
      navigateExternal(appendRedirectUrl(updatePaymentUrl, returnTo));
    },
    onError: (error) => options?.onError?.(error),
  });

  return {
    start: () => mutation.mutate(),
    isPending: mutation.isPending || mutation.isSuccess,
    errorKind: mutation.error ? classifyPaymentUpdateError(mutation.error) : null,
  };
};
